// Ring 3 (physical webcam) dashboard-independent diagnostic trace.
//
// One ordered, correlation-id-scoped trace per room-entry attempt. Events are
// recorded to a window-scoped buffer, persisted to localStorage under
// `vrs_ring3_diag`, and mirrored to the console with a `[RING3]` prefix so the
// trace is retrievable from DevTools / Hermes after a physical reproduction
// without depending on Live Ops/Admin Dashboard telemetry being correct.
//
// This module records only observationally: no camera, retry, grace, fencing,
// or publication behavior is changed by calling these helpers.
//
// No credentials, tokens, SDP, IP addresses, or raw session identifiers are
// recorded. RTP identity is captured only as a boolean (whether an outbound
// video RTP sender was observed), never its SSRC.
const RING3_STORAGE_KEY = 'vrs_ring3_diag';
const RING3_MAX_EVENTS = 200;
const RING3_PREFIX = '[RING3]';
let ring3TraceId = null;
let ring3Seq = 0;
/** Start a new trace for the current room-entry attempt. */
export function newRing3TraceId() {
    ring3TraceId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    ring3Seq = 0;
    return ring3TraceId;
}
/** The active trace id, or null before the first `newRing3TraceId`. */
export function currentRing3TraceId() {
    return ring3TraceId;
}
function ring3Now() {
    return typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? Math.round(performance.now())
        : Date.now();
}
/**
 * Record one ordered trace event. Payload values must already be scrubbed by
 * the caller (only primitives / booleans / short strings).
 */
export function recordRing3Event(phase, payload = {}) {
    const event = {
        id: ring3TraceId || 'unknown',
        seq: ring3Seq++,
        t: ring3Now(),
        phase,
        ...payload
    };
    try {
        // eslint-disable-next-line no-console
        console.log(RING3_PREFIX, JSON.stringify(event));
    }
    catch (e) {
        // Console mirror is best-effort; the window/storage buffer still holds the trace.
    }
    try {
        if (typeof window !== 'undefined') {
            const buf = window.__VRS_RING3__ = window.__VRS_RING3__ || [];
            buf.push(event);
            if (buf.length > RING3_MAX_EVENTS) {
                buf.shift();
            }
            if (window.localStorage) {
                window.localStorage.setItem(RING3_STORAGE_KEY, JSON.stringify(buf));
            }
        }
    }
    catch (e) {
        // Storage/global may be unavailable; the console mirror still holds the trace.
    }
    return event;
}
/** Read the current trace (prefers the live window buffer, falls back to storage). */
export function readRing3Trace() {
    try {
        if (typeof window !== 'undefined' && Array.isArray(window.__VRS_RING3__)) {
            return window.__VRS_RING3__;
        }
        if (typeof window !== 'undefined' && window.localStorage) {
            const raw = window.localStorage.getItem(RING3_STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        }
    }
    catch (e) {
        // noop
    }
    return [];
}
/** Clear the trace (used to reset between physical attempts). */
export function clearRing3Trace() {
    ring3Seq = 0;
    ring3TraceId = null;
    try {
        if (typeof window !== 'undefined') {
            window.__VRS_RING3__ = [];
            if (window.localStorage) {
                window.localStorage.removeItem(RING3_STORAGE_KEY);
            }
        }
    }
    catch (e) {
        // noop
    }
}
// ── Pure classification helpers (Node-testable; no browser globals) ──────
/** Classify whether initial tracks arrived inside the join-first grace window. */
export function classifyRing3Grace(arrivedInGrace) {
    return arrivedInGrace ? 'before_grace' : 'after_grace';
}
/**
 * Classify the `useVideoStream` fence decision. A `false` isCurrent means the
 * requesting lifecycle is no longer active; a `false` roomMatches means the
 * room has since changed (the queued camera response belongs to an earlier call).
 */
export function classifyRing3Fence(isCurrent, roomMatches) {
    if (!isCurrent) {
        return 'dispose_not_current';
    }
    if (!roomMatches) {
        return 'dispose_stale_room';
    }
    return 'accept';
}
/**
 * Classify the bounded post-publication outbound-video sample. `null`/`undefined`
 * means no stats could be read; otherwise any positive counter proves encoding/
 * transmission actually advanced.
 */
export function classifyRing3Publication(stats) {
    if (!stats) {
        return 'absent';
    }
    const framesEncoded = typeof stats.framesEncoded === 'number' ? stats.framesEncoded : 0;
    const bytesSent = typeof stats.bytesSent === 'number' ? stats.bytesSent : 0;
    const framesSent = typeof stats.framesSent === 'number' ? stats.framesSent : 0;
    if (framesEncoded > 0 || bytesSent > 0 || framesSent > 0) {
        return 'progressive';
    }
    return 'flat';
}
